/* src/components/tutor/tutor_announcements/dashboard/TutorAnnouncementsDashboard.js */
import React, { useEffect, useMemo, useState } from "react";
import {
  subscribeTutorAnnouncements,
  subscribeCategories,
} from "../TutorAnnouncementsLogic";

import TutorAnnouncementCard from "./TutorAnnouncementCard";
import ToastHost from "./ToastHost";
import { FiSearch } from "react-icons/fi";
import "./AnnouncementsDashboard.css";

function safeCategory(cat) {
  const c = (cat || "").trim();
  return c || "General";
}

function toMs(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  return null;
}

function isToday(ms) {
  if (!ms) return false;
  const d = new Date(ms);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export default function TutorAnnouncementsDashboard({ me, onOpenPost }) {
  const [announcements, setAnnouncements] = useState([]);
  const [savedCategories, setSavedCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!me?.uid) return;
    const unsub = subscribeTutorAnnouncements(me.uid, (rows) => setAnnouncements(rows || []));
    return () => unsub?.();
  }, [me?.uid]);

  useEffect(() => {
    const unsub = subscribeCategories((rows) => setSavedCategories(rows || []));
    return () => unsub?.();
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set();
    set.add("General");
    set.add("Updates");
    (savedCategories || []).forEach((c) => set.add(c.name));
    (announcements || []).forEach((a) => set.add(safeCategory(a.category)));
    const rest = [...set]
      .filter((c) => c !== "General" && c !== "Updates")
      .sort((a, b) => a.localeCompare(b));
    return ["General", "Updates", ...rest];
  }, [announcements, savedCategories]);

  const filteredFeed = useMemo(() => {
    // Tutors never see archived
    let rows = (announcements || []).filter((a) => !a.archived);

    if (activeFilter === "pinned") {
      rows = rows.filter((a) => !!a.pinned);
    } else if (activeFilter !== "all") {
      rows = rows.filter((a) => safeCategory(a.category) === activeFilter);
    }

    // Search
    const q = (searchQuery || "").trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) => {
        const title = (a.title || "").toLowerCase();
        const preview = (a.contentText || a.contentHtml || "").toLowerCase();
        const author = (a.createdByName || "").toLowerCase();
        return title.includes(q) || preview.includes(q) || author.includes(q);
      });
    }

    // Sort newest first
    rows.sort((a, b) => {
      const ao = a.createdAtMs || toMs(a.createdAt) || 0;
      const bo = b.createdAtMs || toMs(b.createdAt) || 0;
      return bo - ao;
    });

    return rows;
  }, [announcements, activeFilter, searchQuery]);

  const todayItems = useMemo(
    () => filteredFeed.filter((a) => isToday(a.createdAtMs || toMs(a.createdAt))),
    [filteredFeed]
  );
  const earlierItems = useMemo(
    () => filteredFeed.filter((a) => !isToday(a.createdAtMs || toMs(a.createdAt))),
    [filteredFeed]
  );

  const filterPills = [
    { key: "all", label: "All" },
    { key: "pinned", label: "Pinned" },
    ...allCategories.map((c) => ({ key: c, label: c })),
  ];

  return (
    <>
      {/* Topbar */}
      <div className="aaDashTopbar">
        <div className="aaDashTopLeft">
          <h1 className="aaDashTitle">Announcements</h1>
          <p className="aaDashSubtitle">Stay up to date with the latest updates</p>
        </div>
        <div className="aaDashTopRight">
          <div className="aaDashSearchWrap">
            <FiSearch size={15} className="aaDashSearchIcon" />
            <input
              className="aaDashSearchInput"
              placeholder="Search announcements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="aaDashPillsRow">
        {filterPills.map((p) => (
          <button
            key={p.key}
            className={`aaDashPill ${activeFilter === p.key ? "active" : ""}`}
            onClick={() => setActiveFilter(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Feed */}
      <div className="aaDashFeed">
        {filteredFeed.length === 0 ? (
          <div className="aaDashEmpty">No announcements in this view.</div>
        ) : (
          <>
            {todayItems.length > 0 && (
              <div className="aaDashSection">
                <div className="aaDashSectionLabel">TODAY</div>
                {todayItems.map((a) => (
                  <TutorAnnouncementCard
                    key={a.id}
                    me={me}
                    announcement={a}
                    onOpenPost={() => onOpenPost?.(a.id)}
                    onToast={setToast}
                  />
                ))}
              </div>
            )}
            {earlierItems.length > 0 && (
              <div className="aaDashSection">
                {filteredFeed.length > todayItems.length && (
                  <div className="aaDashSectionLabel">EARLIER</div>
                )}
                {earlierItems.map((a) => (
                  <TutorAnnouncementCard
                    key={a.id}
                    me={me}
                    announcement={a}
                    onOpenPost={() => onOpenPost?.(a.id)}
                    onToast={setToast}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ToastHost toast={toast} onClear={() => setToast(null)} />
    </>
  );
}
